import { create } from 'zustand';
import type { AuthUser, Compania } from '@/shared/types';
import { resetAllUIStores } from '@/shared/stores/ui';

const STORAGE_KEY = 'ef-auth-v2';
const LEGACY_KEY = 'ef-auth-storage';

/**
 * Permiso que habilita los módulos de estados financieros (P&L):
 * Comparativo Mensual, Comparativo Anual, Balance General y Análisis.
 * Se asigna desde el portal (PORTAL_GRUPOCORIS).
 */
export const PERMISSION_VIEW_ESTADOS_FINANCIEROS =
  'access_view_estados_financieros';

/**
 * Regla única de acceso a los módulos de estados financieros (P&L):
 * permiso de vista explícito o el de gestión (quien gestiona, también ve).
 */
export function userCanViewFinancials(
  user: Pick<AuthUser, 'permissions'> | null
): boolean {
  return (
    user?.permissions?.includes(PERMISSION_VIEW_ESTADOS_FINANCIEROS) ||
    user?.permissions?.includes('access_manage_estados_financieros') ||
    false
  );
}

interface PersistedAuth {
  token: string | null;
  user: AuthUser | null;
  isAuthenticated: boolean;
  selectedCompany: Compania | null;
}

interface AuthState extends PersistedAuth {
  isLoading: boolean;

  setAuth: (user: AuthUser, token: string) => void;
  setToken: (token: string) => void;
  setUser: (user: AuthUser) => void;
  setSelectedCompany: (company: Compania) => void;
  logout: () => void;
  setLoading: (loading: boolean) => void;

  hasRole: (role: string) => boolean;
  hasPermission: (permission: string) => boolean;
  canManage: () => boolean;
  canManagePeriods: () => boolean;
  canViewFinancials: () => boolean;
}

function loadPersisted(): PersistedAuth {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (raw) {
      const parsed = JSON.parse(raw) as PersistedAuth;
      return {
        token: parsed.token ?? null,
        user: parsed.user ?? null,
        isAuthenticated: parsed.isAuthenticated ?? false,
        selectedCompany: parsed.selectedCompany ?? null,
      };
    }
  } catch {
    // ignore corrupt storage
  }

  return {
    token: null,
    user: null,
    isAuthenticated: false,
    selectedCompany: null,
  };
}

function savePersisted(state: PersistedAuth) {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(state));
  } catch {
    // ignore
  }
}

try {
  localStorage.removeItem(LEGACY_KEY);
} catch {
  // ignore
}

const initial = loadPersisted();

export const useAuthStore = create<AuthState>((set, get) => ({
  ...initial,
  isLoading: true,

  setAuth: (user, token) => {
    const next: PersistedAuth = {
      user,
      token,
      isAuthenticated: true,
      selectedCompany: user.companias?.[0] || null,
    };
    savePersisted(next);
    set({ ...next, isLoading: false });
  },

  setToken: token => {
    const current = get();
    const next: PersistedAuth = { ...current, token };
    savePersisted(next);
    set({ token });
  },

  setUser: user => {
    const current = get();
    // Evita re-renders si nada relevante cambió (p.ej. hot reload), pero
    // deja pasar cambios de permisos/roles/compañías del servidor.
    if (
      current.user?.id === user.id &&
      current.user?.nombre === user.nombre &&
      JSON.stringify(current.user?.permissions) ===
        JSON.stringify(user.permissions) &&
      JSON.stringify(current.user?.roles) === JSON.stringify(user.roles) &&
      JSON.stringify(current.user?.companias) ===
        JSON.stringify(user.companias) &&
      current.isAuthenticated
    ) {
      return;
    }
    const next: PersistedAuth = {
      ...current,
      user,
      isAuthenticated: true,
    };
    savePersisted(next);
    set({ user, isAuthenticated: true, isLoading: false });
  },

  setSelectedCompany: company => {
    const current = get();
    if (current.selectedCompany?.codigo === company.codigo) return;
    resetAllUIStores();
    const next: PersistedAuth = { ...current, selectedCompany: company };
    savePersisted(next);
    set({ selectedCompany: company });
  },

  logout: () => {
    resetAllUIStores();
    const next: PersistedAuth = {
      user: null,
      token: null,
      isAuthenticated: false,
      selectedCompany: null,
    };
    savePersisted(next);
    set({ ...next, isLoading: false });
  },

  setLoading: loading => set({ isLoading: loading }),

  hasRole: role => {
    const { user } = get();
    return user?.roles?.includes(role) ?? false;
  },

  hasPermission: permission => {
    const { user } = get();
    return user?.permissions?.includes(permission) ?? false;
  },

  canManage: () => {
    const { user } = get();
    return (
      user?.permissions?.includes('access_manage_estados_financieros') ?? false
    );
  },

  canManagePeriods: () => {
    const { user } = get();
    return (
      user?.permissions?.includes('access_periodos_estados_financieros') ??
      false
    );
  },

  canViewFinancials: () => {
    return userCanViewFinancials(get().user);
  },
}));
